import React, {useEffect, useState} from 'react';
import {
    Grid,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Button,
    Typography,
} from '@material-ui/core';
import {
    MyAccordion,
    MyAccordionSummary,
    MyAccordionDetails,
} from '../Accordions';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {NavLink} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {H6} from 'components/TypographyShortcuts';
import InfoModal from 'components/widgets/tooltips/InfoModal';
import {useGetProfile, usePutApplicationChecklist} from 'hooks';
import sectionContent from 'components/widgets/checklists/ChecklistSectionContent';
import profileFields from 'components/forms/profile/fields';
import {toSnakeCase} from 'components/forms/locale/utils';
import {orderOfSections} from 'components/forms/cg719b/FormCG719B';
import {useTheme} from '@material-ui/core/styles';
import validateForm from 'components/forms/utils/validateForm';

const useStyles = makeStyles(theme => ({
    root: {
        [theme.breakpoints.down('sm')]: {
            width: '20em',
            height: '15em',
        },
        [theme.breakpoints.up('md')]: {
            width: '40em',
            height: '30em',
        },
    },
    accordion: {
        justifyContent: 'stretch',
        background: theme.palette.gray.light,
        '&.complete': {
            background:
                theme.palette.type === 'dark'
                    ? theme.palette.success.dark
                    : theme.palette.success.light,
        },
    },
    summary: {
        background: theme.palette.gray.light,
        '&.complete': {
            background:
                theme.palette.type === 'dark'
                    ? theme.palette.success.dark
                    : theme.palette.success.light,
        },
    },
    checkbox: {
        color: theme.palette.primary.main,
    },
}));

const ProfileWidget = ({validationErrors}) => {
    const theme = useTheme();

    //profile stuff
    // find incomplete sections
    const incompleteFields = validationErrors?.details?.map(field => {
        return field.context?.label;
    });
    const findIncompleteSections = missingFields => {
        let sections = [];
        orderOfSections?.forEach(section => {
            missingFields?.forEach(field => {
                if (profileFields[field]?.section === section) {
                    sections.includes(section) || sections.push(section);
                }
            });
        });
        return sections;
    };
    const incompleteSections = findIncompleteSections(incompleteFields);

    return validationErrors ? (
        <Grid container>
            <Grid item style={{marginBottom: '1em'}} xs={12}>
                {' '}
                <Typography variant='body2' style={{marginBottom: '1em'}}>
                    <strong>CG719B Incomplete</strong>
                </Typography>
                <Typography
                    variant='body2'
                    style={{color: theme.palette.secondary.main}}
                >
                    It looks like you haven't finished entering all the required
                    information for the CG-719B.
                </Typography>
            </Grid>
            <Grid item container spacing={2} xs={12}>
                <Grid item xs={12} md={6}>
                    <Button
                        variant='contained'
                        style={{
                            variant: 'body1',
                            fontSize: '14px',
                            fontWeight: 'strong',
                        }}
                        color='primary'
                        component={NavLink}
                        to='/forms/cg719b/start'
                        fullWidth
                    >
                        START CG719B FROM BEGINNING
                    </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Button
                        style={{
                            variant: 'body1',
                            fontSize: '14px',
                            fontWeight: 'strong',
                        }}
                        color='secondary'
                        component={NavLink}
                        to={`/forms/cg719b/${toSnakeCase(
                            incompleteSections?.[0]
                        )}`}
                        variant='contained'
                        fullWidth
                    >
                        JUMP TO INCOMPLETE FIELDS
                    </Button>
                </Grid>
                <Grid item />
            </Grid>
        </Grid>
    ) : (
        <Typography variant='body2' style={{margin: '1em 0'}}>
            <strong> Your CG719B is Completed.</strong>
            <Button
                component={NavLink}
                to='/forms/cg719b/start'
                variant='contained'
                color='primary'
                style={{marginLeft: '1em'}}
            >
                Click here to review CG719B
            </Button>
        </Typography>
    );
};

const ChecklistSectionProfile = ({
    sectionId,
    checkoffs,
    complete,
    setComplete,
}) => {
    const classes = useStyles();
    const {put: putApplication, result} = usePutApplicationChecklist();
    const {data: profileData} = useGetProfile();
    const {error: validationErrors} = validateForm(profileFields, profileData);
    const disabled = !!validationErrors;

    const {
        labelId,
        title,
        description,
        secondaryDescription,
        fields,
        infoModalContent,
    } = sectionContent?.[sectionId];

    const pruneData = sourceOfTruth =>
        fields.reduce((object, item) => {
            return {
                ...object,
                [item]: sourceOfTruth?.application_checklist[item] || false,
            };
        }, {});
    const sourceOfTruth = result || checkoffs;
    const [prunedData, setPrunedData] = useState(pruneData(sourceOfTruth));

    const handleChange = (field, newValue) => {
        const newData = {
            ...prunedData,
            [field]: newValue !== undefined ? newValue : !prunedData[field],
        };
        setPrunedData(newData);
        putApplication(newData);
    };

    useEffect(() => {
        if (result) {
            const thisSectionComplete =
                result?.application_checklist?.[labelId];
            setComplete(thisSectionComplete);
        } else {
            const thisSectionComplete =
                checkoffs?.application_checklist?.[labelId];
            setComplete(thisSectionComplete);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoffs, result]);

    // Handle the POST updates in case a checkbox isn't allowed to be checked
    useEffect(() => {
        if (result || checkoffs) {
            setPrunedData(pruneData(sourceOfTruth));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoffs, result, sourceOfTruth]);

    useEffect(() => {
        if (validationErrors && prunedData[labelId]) {
            handleChange(labelId, false);
        }
        //eslint-disable-next-line
    }, [validationErrors, prunedData]);

    return (
        <MyAccordion
            className={`${classes.accordion} ${complete ? 'complete' : ''}`}
        >
            <MyAccordionSummary
                className={`${classes.summary} ${complete ? 'complete' : ''}`}
                expandIcon={<ExpandMoreIcon />}
            >
                <Grid container className={classes.titleContainer}>
                    <Grid
                        item
                        style={{
                            display: 'flex',
                            justifyContent: 'left',
                        }}
                        xs={12}
                    >
                        {title ? (
                            <H6>
                                <strong>{title}</strong>
                            </H6>
                        ) : null}
                        <InfoModal content={infoModalContent} />
                    </Grid>
                </Grid>
            </MyAccordionSummary>
            <MyAccordionDetails
                className={`${classes.summary} ${complete ? 'complete' : ''}`}
            >
                <ListItem key={labelId} role='button' dense button>
                    <ListItemIcon
                        onClick={
                            disabled
                                ? () => {}
                                : () => {
                                      handleChange(labelId);
                                  }
                        }
                        style={{alignSelf: 'flex-start'}}
                    >
                        <Checkbox
                            className={classes.checkbox}
                            edge='start'
                            checked={prunedData[labelId]}
                            disableRipple
                            inputProps={{
                                'aria-labelledby': labelId,
                            }}
                            disabled={disabled}
                        />
                    </ListItemIcon>

                    <Grid container direction='column'>
                        {description || secondaryDescription ? (
                            <Grid item>
                                <ListItemText
                                    primary={description}
                                    secondary={secondaryDescription}
                                />
                            </Grid>
                        ) : null}
                        <ProfileWidget validationErrors={validationErrors} />
                    </Grid>
                </ListItem>
            </MyAccordionDetails>
        </MyAccordion>
    );
};
export default ChecklistSectionProfile;
